// Survive third-party DOM mutation of the React tree.
//
// THE CRASH
// ---------
// Sentry kept reporting, from unrelated component trees (ActivityForm, ProfileCard)
// and across several releases:
//
//   NotFoundError: Failed to execute 'removeChild' on 'Node':
//   The node to be removed is not a child of this node.
//     at commitDeletionEffectsOnFiber (react-dom-client.production.js)
//
// React 19 throws this during the commit phase when the DOM parent it recorded no
// longer contains the child it is deleting. Nothing in this bundle touches the DOM
// directly (no portals, no appendChild/innerHTML outside analytics.js, which only
// writes to <head>), so the node can only have been moved or replaced by something
// outside the app:
//
//   - Google Translate — the overwhelmingly common cause. It rewrites text nodes
//     into <font> wrappers, so every `<li>RM {price} per person</li>`-style node
//     (text and expression mixed in one element, which this codebase uses heavily)
//     becomes a node React no longer owns. Auto-translate is routine for the
//     Malay/Chinese-reading half of our audience.
//   - In-app browsers. Sentry shows the same users arriving inside Meta's Android
//     IAB, which injects its own scripts into every page it renders.
//   - Browser extensions (ad blockers, password managers, Grammarly…).
//
// The blast radius is the whole app, not the widget: the throw escapes to the root
// Sentry.ErrorBoundary and swaps the entire page for "Something went wrong", so a
// translated page drops the user out of the booking flow completely.
//
// THE FIX
// -------
// Make the two mutation methods React uses tolerate a node that has already been
// moved away, instead of throwing. React's own bookkeeping is already out of step
// with the DOM at that point — the choice is between a stale node lingering in the
// page and the entire app unmounting, and the stale node is far cheaper.
//
// This CANNOT mask a bug of ours: with no direct DOM manipulation in the tree, a
// parent/child mismatch is by definition externally caused. We still report the
// first occurrence per page load so the problem stays visible in Sentry rather
// than being silently swallowed.
//
// The alternative — <meta name="google" content="notranslate"> — also stops the
// crash, but by taking translation away from users who need it. Deliberately not
// taken.
import * as Sentry from "@sentry/react";

let reported = false;
let skipped = 0;

// Report only the FIRST skip per page load: these arrive in bursts (one per node
// React tries to delete), and the 2nd..nth add nothing but Sentry quota.
const report = (method, node, parent) => {
  skipped += 1;
  Sentry.addBreadcrumb({
    category: "dom",
    level: "warning",
    message: `${method}: node is not a child of the given parent (skipped)`,
  });
  if (reported) return;
  reported = true;
  try {
    Sentry.captureMessage(
      `External DOM mutation detected — ${method} skipped to keep the app alive`,
      {
        level: "warning",
        tags: { dom_guard: method, translated: String(isPageTranslated()) },
        extra: {
          method,
          nodeName: node?.nodeName,
          parentName: parent?.nodeName,
          // A <font> wrapper around our text is Google Translate's fingerprint.
          actualParentName: node?.parentNode?.nodeName ?? null,
          htmlLang: document.documentElement.getAttribute("lang"),
          htmlClass: document.documentElement.className,
        },
      }
    );
  } catch (_) {
    // Never let error reporting throw inside a DOM primitive.
  }
};

// Google Translate stamps the <html> element when it rewrites the page. Best-effort
// signal only — it tells us WHICH external agent we are coping with.
const isPageTranslated = () => {
  try {
    const html = document.documentElement;
    return (
      html.classList.contains("translated-ltr") ||
      html.classList.contains("translated-rtl") ||
      Boolean(document.querySelector("font[_msttexthash], font[class*='notranslate']"))
    );
  } catch (_) {
    return false;
  }
};

export function installDomResilience() {
  if (typeof Node === "undefined" || !Node.prototype) return;
  // Idempotent (StrictMode, HMR, or a second copy of this module in the bundle).
  // Non-enumerable so the flag never shows up in a for...in over a DOM node.
  if (Node.prototype.__pdDomResilience) return;
  Object.defineProperty(Node.prototype, "__pdDomResilience", {
    value: true,
    enumerable: false,
    configurable: true,
  });

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      report("removeChild", child, this);
      return child; // React only uses the return value as the detached node.
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    // Same failure one step earlier: React inserts before a sibling that the
    // translator has since re-parented. Append instead of throwing, so the node
    // still lands in the right container even if the order is off.
    if (referenceNode && referenceNode.parentNode !== this) {
      report("insertBefore", referenceNode, this);
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// How many mutations we have had to skip (exported for debugging in the console).
export const domResilienceSkipCount = () => skipped;
